/**
 * AssetLossSelector — Multi-select grid with +/- quantity steppers, grouped by domain
 */

import type { AssetSummary, AssetDetail, Domain } from '../../../../types'
import { DOMAIN_CONFIG } from '../../../../types'

interface AssetLossSelectorProps {
  assets: AssetSummary[]
  assetDetails: Record<string, AssetDetail>
  lostAssets: Record<string, number>   // asset ID → count lost
  onUpdateCount: (assetId: string, delta: number) => void
  onRun: () => void
  isSimulating: boolean
}

export function AssetLossSelector({ assets, assetDetails, lostAssets, onUpdateCount, onRun, isSimulating }: AssetLossSelectorProps) {
  const domains = ['AIR', 'SEA', 'SPACE', 'CYBER', 'GROUND'] as Domain[]
  const typesSelected = Object.keys(lostAssets).length
  const totalUnits = Object.values(lostAssets).reduce((s, n) => s + n, 0)

  return (
    <div className="term-box p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
          Select Assets to Lose ({typesSelected}/5 types)
        </div>
        <div className="flex items-center gap-2">
          {typesSelected > 0 && (
            <span className="text-[10px] text-red-400 font-bold">
              {totalUnits} UNIT{totalUnits > 1 ? 'S' : ''} / {typesSelected} TYPE{typesSelected > 1 ? 'S' : ''} MARKED
            </span>
          )}
          <button
            onClick={onRun}
            disabled={isSimulating || typesSelected === 0}
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded
                       bg-red-500/20 text-red-400 border border-red-500/40
                       hover:bg-red-500/30 transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSimulating ? 'Simulating...' : 'Run Simulation'}
          </button>
        </div>
      </div>

      {domains.map(domain => {
        const domainAssets = assets.filter(a => a.domain === domain)
        if (domainAssets.length === 0) return null
        return (
          <div key={domain} className="mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: DOMAIN_CONFIG[domain].color }}>
              {domain}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {domainAssets.map(a => {
                const count = lostAssets[a.id] ?? 0
                const isActive = count > 0
                const assigned = a.assigned_count
                const atMax = count >= assigned
                const canAdd = !atMax && (isActive || typesSelected < 5)

                return (
                  <div
                    key={a.id}
                    className={`term-box px-2 py-1.5 text-[10px] min-w-[140px] transition-all
                               ${isActive ? 'ring-2 ring-red-500 bg-red-500/10 border-red-500/50' : ''}`}
                  >
                    {/* Asset name */}
                    <div className={`font-bold truncate mb-1 ${isActive ? 'text-red-400' : 'text-term-text'}`}>
                      {a.asset_type.split(' ')[0]}
                    </div>

                    {/* Stepper: [-] count/total [+] */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onUpdateCount(a.id, -1)}
                        disabled={count === 0}
                        className="w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold
                                   bg-term-bgLight border border-term-border text-term-text
                                   hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-400
                                   disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      >
                        −
                      </button>

                      <div className={`flex-1 text-center font-bold tabular-nums ${isActive ? 'text-red-400' : 'text-term-textDim'}`}>
                        {count}/{assigned}
                      </div>

                      <button
                        onClick={() => onUpdateCount(a.id, 1)}
                        disabled={!canAdd}
                        className="w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold
                                   bg-term-bgLight border border-term-border text-term-text
                                   hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-400
                                   disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
